import { Text } from "@ttc3k/trekker";
import { Address, OrderByIdQuery } from "gql/generated";
import { ProfileCircle } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Grid, GridItem } from "styled-system/jsx";
import { ReservationSection } from "./ReservationSection";
import { formatPhoneNumber, getAddressString } from "shared/utils";

type GuestsProps = {
  guests: NonNullable<OrderByIdQuery["orderById"]>["guests"];
  phone?: string;
  email: string;
  name: string;
  address?: Address | null;
  businessName?: string;
  /** Fee / renewable fee orders: "Purchaser Info" instead of "Guest Info". */
  purchaserHeading?: boolean;
};

type ContactFieldRow = {
  label: string;
  value: string;
};

const ContactDivider = () => (
  <Box
    border="1px solid"
    borderColor="border.light"
    flexShrink={0}
    h="2px"
    w="full"
  />
);

const ContactField = ({ label, value }: ContactFieldRow) => (
  <Flex py="250" px="300" flexDir="column" gap="25" w="full" minW={0}>
    <Text visual="smallSemiBold" color="text.light">
      {label}
    </Text>
    <Text
      visual="bodyMedium"
      color="text.mid"
      wordBreak="break-word"
      overflowWrap="anywhere"
    >
      {value}
    </Text>
  </Flex>
);

export const Guests = ({
  guests,
  phone,
  email,
  name,
  address,
  businessName,
  purchaserHeading = false,
}: GuestsProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.GUESTS",
  });

  const addressString =
    address && (address.line1 || address.city)
      ? getAddressString(address)
      : "";

  const contactRows: ContactFieldRow[] = [];
  if (name.trim()) {
    contactRows.push({ label: t("NAME"), value: name.trim() });
  }
  if (email.trim()) {
    contactRows.push({ label: t("EMAIL"), value: email.trim() });
  }
  if (businessName?.trim()) {
    contactRows.push({ label: t("BUSINESS"), value: businessName.trim() });
  }
  if (phone?.trim()) {
    contactRows.push({
      label: t("PHONE"),
      value: formatPhoneNumber(phone),
    });
  }
  if (addressString) {
    contactRows.push({ label: t("ADDRESS"), value: addressString });
  }

  return (
    <ReservationSection
      icon={ProfileCircle}
      title={t(purchaserHeading ? "PURCHASER_TITLE" : "TITLE")}
    >
      <Flex
        gap="0"
        flexDir="column"
        w="full"
        alignSelf="stretch"
        bgColor="ink.100"
        border="2px solid"
        borderColor="border.light"
        borderRadius="100"
      >
        {contactRows.map((row, index) => (
          <Box key={row.label} w="full" minW={0}>
            {index > 0 && <ContactDivider />}
            <ContactField {...row} />
          </Box>
        ))}
      </Flex>
      {!!guests && guests.length > 0 && !!guests[0]?.name && (
        <Grid
          gridTemplateColumns="repeat(3, 1fr)"
          py="250"
          px="300"
          w="full"
          bgColor="ink.100"
          border="2px solid"
          borderColor="border.light"
          borderRadius="100"
        >
          <GridItem colSpan={3}>
            <Text visual={"smallSemiBold"} color={"text.light"}>
              {t("ADDITIONAL_GUESTS")}
            </Text>
          </GridItem>
          {guests
            .filter(guest => !!guest)
            .map((guest, i) => (
              <Flex key={i} flexDir="column" gap="25">
                <Text visual="bodyMedium" color="text.mid">
                  {guest.name}
                </Text>
                {!!guest.email?.trim() && (
                  <Text
                    visual="smallRegular"
                    color="text.light"
                    wordBreak="break-word"
                    overflowWrap="anywhere"
                  >
                    {guest.email}
                  </Text>
                )}
              </Flex>
            ))}
        </Grid>
      )}
    </ReservationSection>
  );
};
